import * as R from 'remeda';
import { ThemeType } from '../../../../constants/values';
import {
  createPropertySetter,
  PropertiesManager,
} from '../../propertiesManager';
import { updateWithSchema } from '../../utils';
import { SupportMenuItem } from '../orgUiSettingsSchema';
import {
  customBrandNameSchema,
  customLoaderSchema,
  customLogoImageSchema,
  customUserAvatarImageSchema,
  defaultThemeSchema,
  displayAuroraBrandingSchema,
  displayRossumBrandingSchema,
  fontFamilySchema,
  primaryColorSchema,
  showExternalResourcesSchema,
  squareSchema,
  supportMenuSchema,
} from './schemas';

export type OrganizationBranding = {
  defaultTheme: 'dark' | 'light' | 'coupa' | null;
  primaryColor: string | null;
  fontFamily: string | null;
  /** @deprecated we are phasing out custom loaders, do not use in new places */
  customLoaderImage: string | null;
  displayRossumBranding: boolean;
  displayAuroraBranding: boolean;
  customLogoImage: string | null;
  customBrandName: string | null;
  customUserAvatarImage: string | null;
  showExternalResources: boolean;
  supportMenuItems: SupportMenuItem[] | null;
  // anyone who is using border radiuses uses all 0s and that's the only thing it does anyway
  // let's call it what it is
  square: boolean;
};

export const oldThemeTypeToNewTheme = (
  theme: ThemeType | undefined
): 'dark' | 'light' | 'coupa' | null => {
  if (!theme) {
    return null;
  }
  if (theme === 'white') {
    return 'light';
  }
  if (theme === 'coupa') {
    return 'coupa';
  }
  return 'dark';
};

export const newThemeToOldTheme = (theme: 'dark' | 'light' | 'coupa') => {
  if (theme === 'light') {
    return 'white' as const;
  }
  if (theme === 'coupa') {
    return 'coupa' as const;
  }
  return 'dark' as const;
};

type OrganizationBrandingManager = PropertiesManager<
  OrganizationBranding,
  unknown
>;

export const organizationBranding: OrganizationBrandingManager = {
  defaultTheme: uiSettings => {
    return oldThemeTypeToNewTheme(defaultThemeSchema.parse(uiSettings).theme);
  },
  setDefaultTheme: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(defaultThemeSchema, uiSettings, draft => {
      draft.theme = R.isNullish(value) ? undefined : newThemeToOldTheme(value);
    });
  }),
  primaryColor: uiSettings => {
    return (
      primaryColorSchema.parse(uiSettings).branding?.colors?.primary ?? null
    );
  },
  setPrimaryColor: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(primaryColorSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.colors = draft.branding.colors ?? {};
      draft.branding.colors.primary = R.isNullish(value) ? undefined : value;
    });
  }),
  fontFamily: uiSettings => {
    return fontFamilySchema.parse(uiSettings).branding?.font ?? null;
  },
  setFontFamily: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(fontFamilySchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.font = R.isNullish(value) ? undefined : value;
    });
  }),
  customLoaderImage: uiSettings => {
    return customLoaderSchema.parse(uiSettings).branding?.loader ?? null;
  },
  setCustomLoaderImage: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(customLoaderSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.loader = R.isNullish(value) ? undefined : value;
    });
  }),
  displayRossumBranding: uiSettings => {
    return (
      displayRossumBrandingSchema.parse(uiSettings).branding?.powered_by ?? true
    );
  },
  setDisplayRossumBranding: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(displayRossumBrandingSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.powered_by = R.isNullish(value) ? undefined : !!value;
    });
  }),
  displayAuroraBranding: uiSettings => {
    return (
      displayAuroraBrandingSchema.parse(uiSettings).branding
        ?.powered_by_aurora ?? true
    );
  },
  setDisplayAuroraBranding: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(displayAuroraBrandingSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.powered_by_aurora = R.isNullish(value) ? undefined : value;
    });
  }),
  customLogoImage: uiSettings => {
    return customLogoImageSchema.parse(uiSettings).branding?.top_left ?? null;
  },
  setCustomLogoImage: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(customLogoImageSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.top_left = R.isNullish(value) ? undefined : value;
    });
  }),
  customBrandName: uiSettings => {
    return customBrandNameSchema.parse(uiSettings).branding?.brand_name ?? null;
  },
  setCustomBrandName: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(customBrandNameSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.brand_name = R.isNullish(value) ? undefined : value;
    });
  }),
  customUserAvatarImage: uiSettings => {
    return (
      customUserAvatarImageSchema.parse(uiSettings).branding?.top_right ?? null
    );
  },
  setCustomUserAvatarImage: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(customUserAvatarImageSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.top_right = R.isNullish(value) ? undefined : value;
    });
  }),
  showExternalResources: uiSettings => {
    return !showExternalResourcesSchema.parse(uiSettings).branding
      ?.hide_article_links;
  },
  setShowExternalResources: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(showExternalResourcesSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.hide_article_links = R.isNullish(value)
        ? undefined
        : !value;
    });
  }),
  supportMenuItems: uiSettings => {
    return supportMenuSchema.parse(uiSettings).branding?.support_menu ?? null;
  },
  setSupportMenuItems: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(supportMenuSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.support_menu = R.isNullish(value) ? undefined : value;
    });
  }),
  // if anyone has radiuses set, it's always all 0s and that's the only thing it does anyway
  // let's call it what it is
  square: uiSettings => {
    return !!squareSchema.parse(uiSettings).branding?.border_radiuses;
  },
  setSquare: createPropertySetter((uiSettings, value) => {
    return updateWithSchema(squareSchema, uiSettings, draft => {
      draft.branding = draft.branding ?? {};
      draft.branding.border_radiuses = R.isNullish(value)
        ? undefined
        : value
          ? {}
          : undefined;
    });
  }),
};
